import React from 'react'
import { CalibrationChart } from './charts/CalibrationChart'
import {
  getTestTypeDisplayName,
  isThreePointCalibration,
  isOnePointCalibration
} from '@lura/shared'
import type { DeviceTest } from '@lura/shared'

const BUFFER_COLORS: Record<number, string> = {
  4: 'var(--color-buffer-red)',
  7: 'var(--color-buffer-purple)',
  10: 'var(--color-buffer-blue)'
}

function downloadTestData(test: DeviceTest) {
  const is3Point = isThreePointCalibration(test)
  const buffers = is3Point
    ? test.data.buffers
    : isOnePointCalibration(test)
      ? [test.data.buffer]
      : []

  const sensitivity = is3Point ? test.data.sensitivity_mv_per_ph : 0
  const offset = test.data.offset_mv
  const linearity = is3Point ? test.data.linearity_r_squared : 1.0

  const exportData = {
    test_id: test.id,
    device_id: test.device_id,
    test_type: test.test_type,
    created_at: test.created_at,
    buffers: buffers.map(b => ({
      nominal_ph: b.ph,
      measured_ph: b.measured_ph,
      voltage_mv: b.voltage_mv,
      deviation: Math.abs(b.measured_ph - b.ph)
    })),
    calculated_values: {
      sensitivity_mv_per_ph: Math.abs(sensitivity),
      offset_mv: offset,
      ...(is3Point && { linearity_r_squared: linearity })
    }
  }

  const blob = new Blob([JSON.stringify(exportData, null, 2)], {
    type: 'application/json'
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `calibration-test-${test.id.slice(0, 8)}-${new Date(test.created_at).toISOString().split('T')[0]}.json`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

interface DeviceTestCardProps {
  test: DeviceTest
}

const DeviceTestCard: React.FC<DeviceTestCardProps> = ({ test }) => {
  const is3Point = isThreePointCalibration(test)
  const buffers = is3Point
    ? test.data.buffers
    : isOnePointCalibration(test)
      ? [test.data.buffer]
      : []

  const sensitivity = is3Point ? test.data.sensitivity_mv_per_ph : 0
  const offset = test.data.offset_mv
  const linearity = is3Point ? test.data.linearity_r_squared : 1.0

  return (
    <div className="bg-white rounded-3xl p-6 shadow-sm">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3
            className="text-xl font-semibold text-gray-800"
            style={{ fontFamily: 'Family' }}
          >
            {getTestTypeDisplayName(test.test_type)}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {new Date(test.created_at).toLocaleDateString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: '2-digit'
            })}{' '}
            at{' '}
            {new Date(test.created_at).toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              hour12: true
            })}
          </p>
        </div>
        <button
          onClick={() => downloadTestData(test)}
          className="px-4 py-2 bg-dark-blue text-white text-sm font-medium rounded-full hover:bg-opacity-90 transition-colors"
        >
          Download Test Data
        </button>
      </div>

      {/* Content */}
      <div className="grid gap-6 grid-cols-1 lg:grid-cols-3">
        {/* Buffer Cards Column */}
        <div className="space-y-4">
          {buffers.map(buffer => (
              <div
                key={buffer.ph}
                className="bg-white rounded-xl p-4 shadow-2xs"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{
                        backgroundColor:
                          BUFFER_COLORS[buffer.ph as keyof typeof BUFFER_COLORS] || '#6b7280'
                      }}
                    />
                    <span className="text-sm font-medium text-gray-700">
                      pH {buffer.ph} Buffer
                    </span>
                  </div>
                </div>
                <div className="mt-2">
                  <span className="text-2xl font-bold text-gray-800">
                    {buffer.voltage_mv.toFixed(1)} mV
                  </span>
                </div>
              </div>
          ))}
        </div>

        {/* Stats Column */}
        <div className="space-y-4">
          {is3Point && (
            <div className="bg-white rounded-xl p-4 shadow-2xs flex justify-between items-center">
              <span className="text-sm text-gray-600">Sensitivity (mV/pH)</span>
              <span className="text-sm font-medium text-gray-800">{Math.abs(sensitivity).toFixed(1)}</span>
            </div>
          )}
          <div className="bg-white rounded-xl p-4 shadow-2xs flex justify-between items-center">
            <span className="text-sm text-gray-600">Offset (mV)</span>
            <span className="text-sm font-medium text-gray-800">{offset.toFixed(1)}</span>
          </div>
          {is3Point && (
            <div className="bg-white rounded-xl p-4 shadow-2xs flex justify-between items-center">
              <span className="text-sm text-gray-600">Linearity (R²)</span>
              <span className="text-sm font-medium text-gray-800">{linearity.toFixed(3)}</span>
            </div>
          )}
        </div>

        {/* Calibration Graph Column (3-point only) */}
        {is3Point && (
          <CalibrationChart
            readings={buffers.map(b => ({
              ph: b.ph,
              voltage_mv: b.voltage_mv,
              measured_ph: b.measured_ph
            }))}
          />
        )}
      </div>
    </div>
  )
}

interface DeviceTestsTabProps {
  tests: DeviceTest[]
  loading: boolean
}

export const DeviceTestsTab: React.FC<DeviceTestsTabProps> = ({ tests, loading }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-3xl p-6 shadow-sm">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500"></div>
        </div>
      </div>
    )
  }

  if (tests.length === 0) {
    return (
      <div className="bg-white rounded-3xl p-6 shadow-sm">
        <div className="py-12 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
            />
          </svg>
          <p className="text-sm text-gray-500">
            No tests have been performed on this device yet
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {tests.map(test => (
        <DeviceTestCard key={test.id} test={test} />
      ))}
    </div>
  )
}
